import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { IconFileTypePdf, IconRosetteDiscountCheck } from '@tabler/icons-react';
import { Button, Center, Text } from '@mantine/core';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Page, ProcessHeader } from '@/components';
import { useDownloadPdfMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import LottieAnimation from '@/components/LottieAnimation';
import successLottie from '@/assets/images/success.lottie.json';
import { updateProposal } from '@/data/features/proposal/proposal';
import vars from '@/config/vars';

// Loan types that trigger a referral flow: summary link is not sent to the client.
const LOAN_TYPES_REFERRAL = ['reverseMortgage', 'businessCredit'];

const HEADER_LABELS = {
  referral: 'הפניה',
  offer: 'הצעה',
};

const Success = () => {
  const { isReferral: param } = useParams();

  const { t } = useTranslation();
  const proposalOffer = useSelector((state) => state.proposal);
  const initialProposalOffer = useRef(proposalOffer);
  const [loading, setLoading] = useState(false);
  const [pdfApiConfig] = useDownloadPdfMutation();
  const dispatch = useDispatch();
  const { loanTypeName, cycleMortgage, routeType } = initialProposalOffer.current.extraDetails;
  const { mortgageCycleAction } = cycleMortgage || '';
  const REFERRAL_TYPE_MESSAGES = {
    businessCredit: t('pages.success.message.businessCredit'),
    reverseMortgage: t('pages.success.message.reverseMortgage'),
    mortgageRefinancing: t('pages.success.message.mortgageRefinancing'),
    isReferral: t('pages.success.message.referral'),
  };

  useEffect(() => {
    dispatch({ type: 'RESET_ALL' });
    dispatch(updateProposal({ oldId: proposalOffer.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPdf = async () => {
    setLoading(true);

    try {
      const dataForPdf = {
        id: proposalOffer.oldId,
      };
      const { data } = await getMutationResult(dataForPdf, pdfApiConfig);

      // Convert the JSON object to a Uint8Array
      const byteArray = new Uint8Array(Object.values(data.data));

      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'proposal.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      /* empty */
    } finally {
      setLoading(false);
    }
  };

  const getLoanDisplayData = (loanType) => {
    const isReducingCycleMortgage = loanType === 'mortgageRefinancing' && mortgageCycleAction === 'reducingTheLoanPeriod';
    const isReferral = isReducingCycleMortgage || (LOAN_TYPES_REFERRAL.includes(loanType)) || param === 'true';
    return {
      isReferral,
      label: isReferral ? HEADER_LABELS.referral : HEADER_LABELS.offer,
      subtitle: isReferral ? null : t('pages.success.subtitle'),
      message: (() => {
        if (param === 'true') {
          return REFERRAL_TYPE_MESSAGES.isReferral;
        }
        if (isReferral) {
          return REFERRAL_TYPE_MESSAGES[loanType];
        }
        return '';
      })(),
    };
  };

  const { label: headerLabel, subtitle, message: offerMessage } = getLoanDisplayData(
    loanTypeName,
  );

  return (
    <ProcessHeader
      headerText={t('pages.success.title', { description: headerLabel })}
      headerColor="#F6EBFF"
      headerTextColor="#9F1FF4"
      subtitle={subtitle}
      icon={IconRosetteDiscountCheck}
    >

      <Page.Section withBackground>
        <Center>
          <Text mt={'8'} fw={'bold'} c={'var(--mantine-color-green-6)'}>
            {offerMessage}
          </Text>
          <LottieAnimation lottie={successLottie} height="250px" width="250px" />
        </Center>
      </Page.Section>

      {vars.env !== 'production' ? (
        <Page.Actions>
          <Button
            variant="light"
            onClick={downloadPdf}
            loading={loading}
            leftSection={<IconFileTypePdf size={16} />}
          >
            {t('pages.success.button.downloadPdf')}
          </Button>
        </Page.Actions>
      )
        : routeType?.name === 'selfSupport' && <Page.Actions>
          <Button
            variant="light"
            onClick={downloadPdf}
            loading={loading}
            leftSection={<IconFileTypePdf size={16} />}
          >
            {t('pages.success.button.downloadPdf')}
          </Button>
        </Page.Actions>
      }
    </ProcessHeader>
  );
};

export default Success;
